import React from "react";
import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSPage,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { useHistory } from "react-router-dom";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryNotifications } from "../../../../query/notifications/queries/useQueryNotifications";
import { useUserId } from "../../../../query/hooks/helpers";
import { useTrackClickNotification } from "../../utils/useTrackClickNotification";
import styles from "./styles.module.scss";
import { selectorRequirementsToApproveNotifications } from "../../selectors/selectorRequirementsToApproveNotifications";
import { useGoToDashboardPayeeRequirementDetails } from "../RouteRequirementDetails/RoutePayeeRequirementDetails";
import { usePayeeRequirementNames } from "../../../Requirements/hooks/usePayeeRequirementNames";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { addIndefiniteArticleToWord } from "../../../../shared/utils/addIndefiniteArticleToWord";

type RequestsToComplete = {
  payeeName: string;
  requirementType: RequirementType;
  requirementDataSourceId: string;
  onClick: () => void;
};

const Item: React.FC<RequestsToComplete> = ({
  payeeName,
  requirementType,
  requirementDataSourceId,
  onClick
}) => {
  const qRequirementName = usePayeeRequirementNames({
    requirementType,
    requirementDataSourceId
  });

  return (
    <WSFlexBox.CenterY justify="space-between">
      <WSElement>
        <WSText weight="medium" pb="M" shimmer={qRequirementName.isLoading}>
          {qRequirementName.name} requirement from {payeeName} awaiting approval
        </WSText>
        <WSText.ParagraphSm>
          The contractor submitted{" "}
          {addIndefiniteArticleToWord(qRequirementName.definitionName)}{" "}
          requirement for your approval. Review the submission and take action.
        </WSText.ParagraphSm>
      </WSElement>
      <WSButton.Secondary size="S" onClick={onClick}>
        View requirement
      </WSButton.Secondary>
    </WSFlexBox.CenterY>
  );
};

export const RoutePendingReviewRequirements: React.FC = () => {
  const history = useHistory();
  const userId = useUserId();
  const trackClickNotification = useTrackClickNotification(userId);
  const queryNotifications = useQueryNotifications();
  const goToPayeeRequirementDetails = useGoToDashboardPayeeRequirementDetails();

  return (
    <WSQueries queries={{ queryNotifications }}>
      {({ queryNotificationsData }) => {
        const requests: RequestsToComplete[] = [];

        const notifications = selectorRequirementsToApproveNotifications(
          queryNotificationsData
        );
        notifications.forEach(notification => {
          requests.push({
            requirementDataSourceId:
              notification.context.requirement.dataSourceId,
            payeeName: notification.context.payee.payeeName,
            requirementType: notification.context.requirement.requirementType,
            onClick: () => {
              trackClickNotification(notification);
              goToPayeeRequirementDetails({
                requirementId: notification.context.requirement.requirementId,
                payeeId: notification.context.payee.payeeId
              });
            }
          });
        });

        return (
          <WSPage
            breadcrumb={{
              label: "Back",
              onClick() {
                history.push("/member/dashboard");
              },
              icon: "arrow-left"
            }}
          >
            <WSText.Heading4 my="XL">
              Contractor requirements awaiting approval ({requests.length})
            </WSText.Heading4>
            {requests.length === 0 ? (
              <WSText.ParagraphSm mt="3XL" align="center" color="gray400">
                No requirements to approve
              </WSText.ParagraphSm>
            ) : (
              requests.map((requirement, index) => {
                return (
                  <WSPanel
                    key={index + requirement.requirementDataSourceId}
                    mb="M"
                    as="label"
                    className={styles.item}
                  >
                    <Item {...requirement} />
                  </WSPanel>
                );
              })
            )}
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
